import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import _ from 'lodash'
import axios from 'axios'
import { widgetsUrl } from 'urls'
import { getAxiosDefaultConfig, getDefaultFilterType, getEffectiveFilterKeys, sanitizeFilters } from 'utils'
import { constructFilterQuery } from 'applications/Dashboard/requests/search'
import { saveConfig } from '../../actions/dashboardActions'
import { initialChartConfigs, initialMetricConfigs } from 'modules/reportConfigs'
import {
  DashboardTab,
  DashboardWidget,
  MAX_TAB_TITLE_LENGTH,
  WidgetData,
  makeTabId,
  makeWidgetId,
  persistableWidget,
  toWidgetSpec,
  widgetContentSignature,
  widgetExecutionKey,
} from 'modules/widgetSpec'

export interface WidgetResult {
  status: 'loading' | 'succeeded' | 'failed'
  data?: WidgetData
  error?: string
  fetchedAt?: number
  /** Present only for generated preview data that exactly matches this execution. */
  executionKey?: string
}

interface DashboardWidgetsState {
  results: Record<string, WidgetResult>
  generateStatus: 'idle' | 'loading' | 'succeeded' | 'failed'
  generateError: string | null
  addedWidgetIds: string[]
  /** Content signatures of added widgets — survives remount when ids are regenerated */
  addedWidgetSignatures: string[]
  /** Opens the Ask AI widget modal from anywhere (e.g. the Add Chart dropdown) */
  askAiOpen: boolean
  /** Which destination the Ask AI flow should preselect */
  askAiPlacement: 'widgets' | 'metrics'
  /** Custom tab the Ask AI flow should add to (null = the Overview page) */
  askAiTabId: string | null
  /** true = the flow builds a NEW custom tab instead of adding to an existing page */
  askAiNewTab: boolean
  /**
   * Tab just created by the AI flow. The Dashboard watches this to switch the strip
   * to the new tab — the modal is several levels below it and has no other way to say
   * "now show this page".
   */
  lastCreatedTabId: string | null
}

export const initialState: DashboardWidgetsState = {
  results: {},
  generateStatus: 'idle',
  generateError: null,
  addedWidgetIds: [],
  addedWidgetSignatures: [],
  askAiOpen: false,
  askAiPlacement: 'widgets',
  askAiTabId: null,
  askAiNewTab: false,
  lastCreatedTabId: null,
}

/** Resolve the dashboard's active date range from redux (same logic as Dashboard.newSearch) */
const getActiveDateRange = (state: any): { start_date?: string; end_date?: string } => {
  try {
    const dateTypes = state.dateFilters?.dateTypes || {}
    const { defaultFilterType } = getDefaultFilterType(dateTypes)
    const dateRange = dateTypes[defaultFilterType]?.dateRange
    if (dateRange?.start && dateRange?.end) return { start_date: dateRange.start, end_date: dateRange.end }
  } catch (e) {
    // fall through — server applies its own default range
  }
  return {}
}

/**
 * Build the same Solr filter query the dashboard applies to its own searches, so a
 * widget reports numbers for the CURRENT filter selection rather than the unfiltered
 * account. Returns '' when no filters are active.
 */
const getDashboardFilterConfig = (state: any): string => {
  try {
    const raw = state.dashboard?.filters
    if (!raw) return ''
    // Drop the keyword pseudo-filters — those ride on `q`, not `fq`.
    const { keywords, ['-keywords']: keywordsExclude, ...filters } = sanitizeFilters(raw)
    const {
      effectiveFilterKeys,
      effectiveOptionFilterKeys,
      effectiveCustomFieldFilterKeys,
      effectiveCustomerFormFieldFilterKeys,
      effectiveShippingFormFieldFilterKeys,
      effectiveCustomerCustomAttributesFilterKeys,
      effectiveOrderMetafieldFilterKeys,
      effectiveProductMetafieldFilterKeys,
    } = getEffectiveFilterKeys(filters)
    const fq = constructFilterQuery(
      filters,
      effectiveFilterKeys,
      effectiveOptionFilterKeys,
      effectiveCustomFieldFilterKeys,
      effectiveCustomerFormFieldFilterKeys,
      effectiveShippingFormFieldFilterKeys,
      effectiveCustomerCustomAttributesFilterKeys,
      state.dashboard?.filterLogicOperator || 'AND',
      effectiveOrderMetafieldFilterKeys || [],
      effectiveProductMetafieldFilterKeys || []
    )
    return typeof fq === 'string' ? fq.trim() : ''
  } catch (e) {
    // A filter we can't translate must not break widget loading
    return ''
  }
}

/**
 * The dashboard's active filter query, memoized on the two slices it is built from.
 * Widgets need it in two places besides the request itself: to know whether to warn
 * that a number can't follow the filters, and as part of their execution key so the
 * ones that CAN follow re-run when the filter changes.
 */
export const selectDashboardFilterConfig = createSelector(
  [(state: any) => state.dashboard?.filters, (state: any) => state.dashboard?.filterLogicOperator],
  (filters, filterLogicOperator) => getDashboardFilterConfig({ dashboard: { filters, filterLogicOperator } })
)

/**
 * Does the filter sidebar currently narrow anything? Cards use this to decide whether
 * to say "unfiltered" — with no filters selected there is nothing to contradict, and
 * the badge would sit on every ads/Stripe widget forever.
 */
export const selectDashboardFiltersActive = (state: any): boolean =>
  selectDashboardFilterConfig(state).length > 0

/** Inclusive day count of the active range — lets widgets do per-day math. */
const getDaysInRange = (range: { start_date?: string; end_date?: string }): number | undefined => {
  if (!range.start_date || !range.end_date) return undefined
  const start = Date.parse(`${range.start_date}T00:00:00Z`)
  const end = Date.parse(`${range.end_date}T00:00:00Z`)
  if (!isFinite(start) || !isFinite(end) || end < start) return undefined
  return Math.floor((end - start) / 86400000) + 1
}

/** Snapshot the already-loaded dashboard summary stats for builtin_stat expression inputs */
const getBuiltinStats = (state: any): Record<string, number> => {
  const stats = state.dashboard?.stats || {}
  const out: Record<string, number> = {}
  for (const key of Object.keys(stats)) {
    const value = stats[key]?.value ?? stats[key]
    if (typeof value === 'number' && isFinite(value)) out[key] = value
  }
  return out
}

const widgetExecutionKeyForState = (widget: DashboardWidget, state: any): string => {
  const dateTypes = state.dateFilters?.dateTypes || {}
  let dateType = ''
  try {
    dateType = getDefaultFilterType(dateTypes).defaultFilterType || ''
  } catch (e) {
    // Keep the same empty fallback used by DashboardWidgets.
  }
  const range = getActiveDateRange(state)
  return widgetExecutionKey(widget, {
    rangeKey: `${dateType}|${range.start_date || ''}|${range.end_date || ''}`,
    builtinStats: getBuiltinStats(state),
    filterConfig: getDashboardFilterConfig(state),
  })
}

/**
 * Execute widget definitions against papaya. Results land in state.dashboardWidgets.results
 * keyed by widget id.
 */
export const executeWidgets = createAsyncThunk(
  'dashboardWidgets/executeWidgets',
  async (
    { widgets, source }: { widgets: DashboardWidget[]; source?: string },
    { getState, rejectWithValue }
  ) => {
    const state: any = getState()
    const range = getActiveDateRange(state)
    const body = {
      widgets,
      ...range,
      builtin_stats: getBuiltinStats(state),
      dashboard_filter_config: getDashboardFilterConfig(state),
      context: { days_in_range: getDaysInRange(range) },
      // Which page this load was for — recorded on the credit-usage row
      source: source || 'dashboard',
    }
    try {
      const res = await axios.post(`${widgetsUrl}/execute`, body, getAxiosDefaultConfig())
      return res.data?.results || {}
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.error || error?.message || 'Failed to load widgets')
    }
  }
)

/**
 * Generating a dashboard is a long request: one model call plus a live preview for
 * every widget, ~20-40s in practice. So it gets a generous explicit timeout (rather
 * than axios's "wait forever") and ONE retry, because the common failure is a dropped
 * connection that returned nothing at all — no work was accepted, so retrying is safe.
 */
const GENERATE_TIMEOUT_MS = 180_000

const postGenerate = async (body: Record<string, any>) => {
  const config = { ...getAxiosDefaultConfig(), timeout: GENERATE_TIMEOUT_MS }
  try {
    return await axios.post(`${widgetsUrl}/generate`, body, config)
  } catch (error: any) {
    // Retry only when nothing came back; a real error response is the answer.
    if (error?.response) throw error
    return axios.post(`${widgetsUrl}/generate`, body, config)
  }
}

export interface WidgetClarify {
  question: string
  options: string[]
}

export interface WidgetIntegrationChoice {
  value: string
  label: string
}

export interface WidgetGenerationContext {
  ga4Known: boolean
  searchConsoleKnown: boolean
  ga4Properties: WidgetIntegrationChoice[]
  searchConsoleSites: WidgetIntegrationChoice[]
}

export interface WidgetAnalyticsSelection {
  ga4PropertyId?: string
  searchConsoleSiteUrl?: string
}

export const fetchWidgetGenerationContext = async (): Promise<WidgetGenerationContext> => {
  const res = await axios.get(`${widgetsUrl}/generation-context`, getAxiosDefaultConfig())
  return {
    ga4Known: !!res.data?.ga4Known,
    searchConsoleKnown: !!res.data?.searchConsoleKnown,
    ga4Properties: Array.isArray(res.data?.ga4Properties) ? res.data.ga4Properties : [],
    searchConsoleSites: Array.isArray(res.data?.searchConsoleSites)
      ? res.data.searchConsoleSites
      : [],
  }
}

export interface GenerateWidgetResult {
  /** Present when the request was clear enough to build (1..N widgets) */
  widgets?: DashboardWidget[]
  /** Present when the AI needs the request qualified first */
  clarify?: WidgetClarify
  /** mode 'tab' only — the name the AI chose for the tab it just designed */
  tabTitle?: string
}

/**
 * Generate one or more widget specs from a natural-language prompt. The endpoint
 * may instead ask a qualifying question; prior answers ride along so the
 * round-trip stays stateless.
 */
export const generateWidget = createAsyncThunk(
  'dashboardWidgets/generateWidget',
  async (
    {
      prompt,
      answers,
      base,
      mode,
      attachment,
      analytics,
    }: {
      prompt: string
      answers?: { question: string; answer: string }[]
      /** Existing widget to revise — the endpoint returns one edited widget */
      base?: DashboardWidget
      /**
       * 'dashboard' asks for a whole page (more widgets, more clarify rounds);
       * 'tab' asks for one focused custom tab, named by the AI
       */
      mode?: 'widget' | 'dashboard' | 'tab'
      /**
       * An uploaded report to rebuild — CSV/spreadsheet rows or a screenshot. The
       * server reads it as a specification and sources every widget from live data.
       */
      attachment?: { name: string; mimeType: string; data: string }
      /** Per-build choices; never persisted as an account-wide default. */
      analytics?: WidgetAnalyticsSelection
    },
    { getState, rejectWithValue }
  ): Promise<GenerateWidgetResult | any> => {
    const state: any = getState()
    const range = getActiveDateRange(state)
    const body = {
      prompt,
      answers,
      mode,
      attachment,
      analytics,
      base: base ? persistableWidget(base) : undefined,
      ...range,
      builtin_stats: getBuiltinStats(state),
      dashboard_filter_config: getDashboardFilterConfig(state),
      context: { days_in_range: getDaysInRange(range) },
    }
    try {
      const res = await postGenerate(body)
      if (!res.data?.ok) {
        return rejectWithValue(res.data?.error || 'Could not generate a widget for that request')
      }
      if (res.data.clarify?.question) {
        return {
          clarify: {
            question: String(res.data.clarify.question),
            options: (res.data.clarify.options || []).map((o: any) => String(o)).filter(Boolean),
          },
        }
      }
      const raw = Array.isArray(res.data.widgets)
        ? res.data.widgets
        : res.data.widget
          ? [res.data.widget]
          : []
      const widgets = raw.map((w: any) => toWidgetSpec(w)).filter(Boolean) as DashboardWidget[]
      if (!widgets.length) return rejectWithValue('The generated widget was invalid — try rephrasing')
      // Carry each widget's preview failure (blocked integration, missing account) so
      // the preview card can say why it is empty instead of showing a dash.
      const previewErrors = res.data.preview_errors || {}
      raw.forEach((rawWidget: any, index: number) => {
        const message = previewErrors[rawWidget?.id]
        if (message && widgets[index]) widgets[index].previewError = String(message)
        if (widgets[index]?.preview) {
          widgets[index].previewExecutionKey = widgetExecutionKeyForState(widgets[index], state)
        }
      })
      const tabTitle =
        typeof res.data.tab?.title === 'string' && res.data.tab.title.trim()
          ? res.data.tab.title.trim().slice(0, MAX_TAB_TITLE_LENGTH)
          : undefined
      return { widgets, ...(tabTitle ? { tabTitle } : {}) }
    } catch (error: any) {
      if (error?.response?.data?.error) return rejectWithValue(error.response.data.error)
      // No response at all: a dropped connection or a timeout, not a server verdict.
      if (!error?.response) {
        return rejectWithValue(
          'The connection dropped before this finished. A whole dashboard can take up to a minute to build — press Generate to try again.'
        )
      }
      return rejectWithValue(error?.message || 'Widget generation failed')
    }
  }
)

/**
 * Persist a widget into dashboardconfig.widgets via the existing dashboard config pipeline.
 * Assigns id/order, strips preview data, saves, and updates redux user config.
 */
/**
 * Merge widgets into an existing widget map, assigning ids/orders and deduping by
 * content signature. Pure so the single- and batch-add thunks share one code path
 * and a batch produces ONE config save (N sequential saves would race, and the
 * last write would clobber the earlier ones).
 */
const mergeWidgets = (
  currentWidgets: Record<string, DashboardWidget>,
  incoming: DashboardWidget[]
): { widgets: Record<string, DashboardWidget>; saved: DashboardWidget[] } => {
  const widgets: Record<string, DashboardWidget> = { ...currentWidgets }
  const saved: DashboardWidget[] = []
  let maxOrder = Object.values(currentWidgets).reduce(
    (max, w: any) => Math.max(max, typeof w?.order === 'number' ? w.order : 0),
    -1
  )
  for (const widget of incoming) {
    const signature = widgetContentSignature(widget)
    const existingId = Object.keys(widgets).find(
      id => widgetContentSignature(widgets[id] as any) === signature
    )
    const targetId = existingId || (widget.id && !widgets[widget.id] ? widget.id : makeWidgetId())
    const order = existingId ? (widgets[existingId] as any).order ?? ++maxOrder : ++maxOrder
    const toSave = persistableWidget({ ...widget, id: targetId, order, visible: true })
    widgets[targetId] = toSave
    saved.push(toSave)
  }
  return { widgets, saved }
}

export const addWidgetToDashboard = createAsyncThunk(
  'dashboardWidgets/addWidgetToDashboard',
  async (widget: DashboardWidget, { getState, dispatch, rejectWithValue }) => {
    const state: any = getState()
    const currentConfig = state.root?.user?.dashboardconfig || {}
    const { widgets, saved } = mergeWidgets(currentConfig.widgets || {}, [widget])
    try {
      await dispatch(saveConfig({ ...currentConfig, widgets }, false))
      // Seed the result cache from the preview so the dashboard renders instantly
      if (widget.preview) {
        dispatch(dashboardWidgetsSlice.actions.seedResult({
          widgetId: saved[0].id,
          data: widget.preview,
          executionKey: widget.previewExecutionKey,
        }))
      }
      return saved[0]
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to save widget to dashboard')
    }
  }
)

/** Persist a whole generated batch in a single config save. */
export const addWidgetsToDashboard = createAsyncThunk(
  'dashboardWidgets/addWidgetsToDashboard',
  async (incoming: DashboardWidget[], { getState, dispatch, rejectWithValue }) => {
    if (!Array.isArray(incoming) || !incoming.length) return []
    const state: any = getState()
    const currentConfig = state.root?.user?.dashboardconfig || {}
    const { widgets, saved } = mergeWidgets(currentConfig.widgets || {}, incoming)
    try {
      await dispatch(saveConfig({ ...currentConfig, widgets }, false))
      saved.forEach((w, i) => {
        const preview = incoming[i]?.preview
        if (preview) {
          dispatch(dashboardWidgetsSlice.actions.seedResult({
            widgetId: w.id,
            data: preview,
            executionKey: incoming[i]?.previewExecutionKey,
          }))
        }
      })
      return saved
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to save widgets to dashboard')
    }
  }
)

/**
 * Replace the dashboard with a generated set: every existing custom widget is
 * hidden, the new ones are written with the layout the generator chose, and the
 * whole thing lands in ONE config save (separate saves would race and the last
 * write would win).
 *
 * `hideBuiltIns` also switches off the built-in metric tiles, facet charts and trend
 * chart, which is what "a completely new dashboard" means — nothing is deleted, so
 * the Add Metric / Add Chart menus and the trend placeholder restore the old page.
 */
export const rebuildDashboard = createAsyncThunk(
  'dashboardWidgets/rebuildDashboard',
  async (
    { widgets, hideBuiltIns }: { widgets: DashboardWidget[]; hideBuiltIns?: boolean },
    { getState, dispatch, rejectWithValue }
  ) => {
    if (!Array.isArray(widgets) || !widgets.length) return []
    const state: any = getState()
    const currentConfig = state.root?.user?.dashboardconfig || {}

    // Retire the current custom widgets (kept, not deleted, so nothing is lost)
    const nextWidgets: Record<string, DashboardWidget> = {}
    Object.entries(currentConfig.widgets || {}).forEach(([id, widget]: [string, any]) => {
      nextWidgets[id] = { ...widget, visible: false }
    })

    // Write the new set in the generator's order, split across the two grids
    const saved: DashboardWidget[] = []
    widgets.forEach((widget, index) => {
      // Reuse the generated id unless it collides with a widget we just retired
      const id = widget.id && !nextWidgets[widget.id] ? widget.id : makeWidgetId()
      const placement = widget.kind === 'metric' ? widget.placement || 'metrics' : 'widgets'
      const toSave = persistableWidget({
        ...widget,
        id,
        placement,
        order: typeof widget.order === 'number' ? widget.order : index,
        // Position inside whichever grid it lands in, in the generator's order
        ...(placement === 'metrics' ? { metricOrder: index } : { gridOrder: index }),
        visible: true,
      })
      nextWidgets[id] = toSave
      saved.push(toSave)
    })

    const nextConfig: Record<string, any> = { ...currentConfig, widgets: nextWidgets }
    if (hideBuiltIns) {
      const hideAll = (group: Record<string, any> | undefined) =>
        Object.keys(group || {}).reduce(
          (acc: Record<string, any>, key) => ({ ...acc, [key]: { ...group?.[key], visible: false } }),
          {}
        )
      nextConfig.metrics = {
        ...currentConfig.metrics,
        overview: hideAll({ ...initialMetricConfigs.overview, ...currentConfig.metrics?.overview }),
      }
      nextConfig.charts = hideAll({ ...initialChartConfigs, ...currentConfig.charts })
      // The built-in trends chart is a built-in block too
      nextConfig.trends = { ...(currentConfig.trends || {}), visible: false }
    }

    try {
      await dispatch(saveConfig(nextConfig, false))
      saved.forEach((widget, index) => {
        const preview = widgets[index]?.preview
        if (preview) {
          dispatch(dashboardWidgetsSlice.actions.seedResult({
            widgetId: widget.id,
            data: preview,
            executionKey: widgets[index]?.previewExecutionKey,
          }))
        }
      })
      return saved
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to rebuild the dashboard')
    }
  }
)

/**
 * Lay a generated set out on a page of its own: metrics into that page's KPI row,
 * everything else into its grid, in the order the generator chose.
 */
const tabWidgetsToSave = (
  widgets: DashboardWidget[],
  tabId: string,
  taken: Record<string, any>,
  startIndex = 0
): DashboardWidget[] =>
  widgets.map((widget, index) => {
    const position = startIndex + index
    const id = widget.id && !taken[widget.id] ? widget.id : makeWidgetId()
    const placement = widget.kind === 'metric' ? widget.placement || 'metrics' : 'widgets'
    const toSave = persistableWidget({
      ...widget,
      id,
      tab: tabId,
      placement,
      order: position,
      ...(placement === 'metrics' ? { metricOrder: position } : { gridOrder: position }),
      visible: true,
    })
    // Reserve the id so a batch can't hand the same one to two widgets
    taken[id] = toSave
    return toSave
  })

const nextTabOrder = (tabsConfig: Record<string, any> | undefined): number =>
  Object.values(tabsConfig || {}).reduce(
    (max: number, tab: any) => Math.max(max, typeof tab?.order === 'number' ? tab.order + 1 : 0),
    0
  )

/**
 * Create a custom tab and fill it with a generated set, in ONE config save (the tab
 * and its widgets are useless apart — a second save could land without the first).
 */
export const createDashboardTab = createAsyncThunk(
  'dashboardWidgets/createDashboardTab',
  async (
    { title, widgets }: { title: string; widgets: DashboardWidget[] },
    { getState, dispatch, rejectWithValue }
  ): Promise<{ tab: DashboardTab; widgets: DashboardWidget[] } | any> => {
    const cleanTitle = String(title || '').replace(/\s+/g, ' ').trim().slice(0, MAX_TAB_TITLE_LENGTH)
    if (!cleanTitle) return rejectWithValue('A tab needs a name')
    const state: any = getState()
    const currentConfig = state.root?.user?.dashboardconfig || {}

    const tabId = makeTabId()
    const tab: DashboardTab = {
      id: tabId,
      title: cleanTitle,
      order: nextTabOrder(currentConfig.tabs),
      visible: true,
    }
    const nextWidgets: Record<string, DashboardWidget> = { ...(currentConfig.widgets || {}) }
    const saved = tabWidgetsToSave(widgets, tabId, nextWidgets)
    saved.forEach(widget => {
      nextWidgets[widget.id] = widget
    })

    try {
      await dispatch(
        saveConfig(
          {
            ...currentConfig,
            tabs: { ...(currentConfig.tabs || {}), [tabId]: tab },
            widgets: nextWidgets,
          },
          false
        )
      )
      saved.forEach((widget, index) => {
        const preview = widgets[index]?.preview
        if (preview) {
          dispatch(dashboardWidgetsSlice.actions.seedResult({
            widgetId: widget.id,
            data: preview,
            executionKey: widgets[index]?.previewExecutionKey,
          }))
        }
      })
      return { tab, widgets: saved }
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to create the tab')
    }
  }
)

/**
 * Add a generated set to an existing custom tab. `replace` retires whatever is on
 * that tab first (kept, not deleted) so a tab can be rebuilt in place.
 */
export const addWidgetsToTab = createAsyncThunk(
  'dashboardWidgets/addWidgetsToTab',
  async (
    { tabId, widgets, replace }: { tabId: string; widgets: DashboardWidget[]; replace?: boolean },
    { getState, dispatch, rejectWithValue }
  ) => {
    if (!tabId || !Array.isArray(widgets) || !widgets.length) return []
    const state: any = getState()
    const currentConfig = state.root?.user?.dashboardconfig || {}

    const nextWidgets: Record<string, DashboardWidget> = {}
    let startIndex = 0
    Object.entries(currentConfig.widgets || {}).forEach(([id, widget]: [string, any]) => {
      const onThisTab = widget?.tab === tabId
      nextWidgets[id] = onThisTab && replace ? { ...widget, visible: false } : widget
      // Appending has to start after the widgets already on the page
      if (onThisTab && !replace && widget?.visible !== false) {
        startIndex = Math.max(startIndex, (typeof widget.order === 'number' ? widget.order : 0) + 1)
      }
    })
    const saved = tabWidgetsToSave(widgets, tabId, nextWidgets, startIndex)
    saved.forEach(widget => {
      nextWidgets[widget.id] = widget
    })

    try {
      await dispatch(saveConfig({ ...currentConfig, widgets: nextWidgets }, false))
      saved.forEach((widget, index) => {
        const preview = widgets[index]?.preview
        if (preview) {
          dispatch(dashboardWidgetsSlice.actions.seedResult({
            widgetId: widget.id,
            data: preview,
            executionKey: widgets[index]?.previewExecutionKey,
          }))
        }
      })
      return saved
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to add widgets to this tab')
    }
  }
)

export const dashboardWidgetsSlice = createSlice({
  name: 'dashboardWidgets',
  initialState,
  reducers: {
    seedResult: (state, action) => {
      const { widgetId, data, executionKey } = action.payload
      state.results[widgetId] = {
        status: 'succeeded',
        data,
        fetchedAt: Date.now(),
        ...(executionKey ? { executionKey } : {}),
      }
    },
    setWidgetResult: (state, action) => {
      const { widgetId, result } = action.payload
      state.results[widgetId] = result
    },
    resetGenerate: state => {
      state.generateStatus = 'idle'
      state.generateError = null
    },
    setAskAiOpen: (state, action) => {
      // Accepts a bool, or { open, placement, tabId, newTab } when the entry point
      // implies a destination (the Add Metric dropdown, a custom tab, "+ New tab").
      const payload = action.payload
      if (payload && typeof payload === 'object') {
        state.askAiOpen = !!payload.open
        if (payload.placement === 'metrics' || payload.placement === 'widgets') {
          state.askAiPlacement = payload.placement
        }
        // Destination is always restated on open, so a stale tab can't be inherited
        state.askAiTabId = typeof payload.tabId === 'string' && payload.tabId ? payload.tabId : null
        state.askAiNewTab = !!payload.newTab
        return
      }
      state.askAiOpen = !!payload
      if (!payload) {
        state.askAiTabId = null
        state.askAiNewTab = false
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(executeWidgets.pending, (state, action) => {
        for (const widget of action.meta.arg.widgets) {
          const existing = state.results[widget.id]
          state.results[widget.id] = { ...(existing || {}), status: 'loading' }
        }
      })
      .addCase(executeWidgets.fulfilled, (state, action) => {
        const results = action.payload as Record<string, { ok: boolean; data?: WidgetData; error?: string }>
        for (const widget of action.meta.arg.widgets) {
          const result = results[widget.id]
          if (result?.ok && result.data) {
            state.results[widget.id] = { status: 'succeeded', data: result.data, fetchedAt: Date.now() }
          } else {
            state.results[widget.id] = {
              ...(state.results[widget.id] || {}),
              status: 'failed',
              error: result?.error || 'No data returned',
            }
          }
        }
      })
      .addCase(executeWidgets.rejected, (state, action) => {
        for (const widget of action.meta.arg.widgets) {
          state.results[widget.id] = {
            ...(state.results[widget.id] || {}),
            status: 'failed',
            error: (action.payload as string) || 'Failed to load widgets',
          }
        }
      })
      .addCase(generateWidget.pending, state => {
        state.generateStatus = 'loading'
        state.generateError = null
      })
      .addCase(generateWidget.fulfilled, state => {
        state.generateStatus = 'succeeded'
      })
      .addCase(generateWidget.rejected, (state, action) => {
        state.generateStatus = 'failed'
        state.generateError = (action.payload as string) || 'Widget generation failed'
      })
      .addCase(addWidgetsToDashboard.fulfilled, (state, action) => {
        for (const widget of action.payload as DashboardWidget[]) {
          if (!state.addedWidgetIds.includes(widget.id)) state.addedWidgetIds.push(widget.id)
          const signature = widgetContentSignature(widget)
          if (!state.addedWidgetSignatures.includes(signature)) {
            state.addedWidgetSignatures.push(signature)
          }
        }
      })
      .addCase(createDashboardTab.fulfilled, (state, action) => {
        const created = action.payload as { tab?: DashboardTab; widgets?: DashboardWidget[] }
        if (created?.tab?.id) state.lastCreatedTabId = created.tab.id
        for (const widget of created?.widgets || []) {
          if (!state.addedWidgetIds.includes(widget.id)) state.addedWidgetIds.push(widget.id)
        }
      })
      .addCase(addWidgetsToTab.fulfilled, (state, action) => {
        for (const widget of (action.payload as DashboardWidget[]) || []) {
          if (!state.addedWidgetIds.includes(widget.id)) state.addedWidgetIds.push(widget.id)
        }
      })
      .addCase(addWidgetToDashboard.fulfilled, (state, action) => {
        if (!state.addedWidgetIds.includes(action.payload.id)) {
          state.addedWidgetIds.push(action.payload.id)
        }
        const signature = widgetContentSignature(action.payload)
        if (!state.addedWidgetSignatures.includes(signature)) {
          state.addedWidgetSignatures.push(signature)
        }
      })
  },
})

export const { seedResult, setWidgetResult, resetGenerate, setAskAiOpen } = dashboardWidgetsSlice.actions

export const selectWidgetResults = (state: any): Record<string, WidgetResult> =>
  state.dashboardWidgets?.results || {}
export const selectWidgetResult = (state: any, widgetId: string): WidgetResult | undefined =>
  state.dashboardWidgets?.results?.[widgetId]
export const selectGenerateStatus = (state: any) => state.dashboardWidgets?.generateStatus || 'idle'
export const selectGenerateError = (state: any) => state.dashboardWidgets?.generateError
export const selectAddedWidgetIds = (state: any): string[] => state.dashboardWidgets?.addedWidgetIds || []
export const selectAddedWidgetSignatures = (state: any): string[] =>
  state.dashboardWidgets?.addedWidgetSignatures || []
export const selectAskAiOpen = (state: any): boolean => !!state.dashboardWidgets?.askAiOpen
export const selectAskAiPlacement = (state: any): 'widgets' | 'metrics' =>
  state.dashboardWidgets?.askAiPlacement === 'metrics' ? 'metrics' : 'widgets'
export const selectAskAiTabId = (state: any): string | null => state.dashboardWidgets?.askAiTabId || null
export const selectAskAiNewTab = (state: any): boolean => !!state.dashboardWidgets?.askAiNewTab
export const selectLastCreatedTabId = (state: any): string | null =>
  state.dashboardWidgets?.lastCreatedTabId || null

export default dashboardWidgetsSlice.reducer
